import type { ReactNode } from 'react';
import { useEffect, useReducer, useState } from 'react';
import type {
  AppHeaderProps,
  NavLinkProps,
} from '@standardsdigital/ui-components/AppHeader';
import AppHeader from '@standardsdigital/ui-components/AppHeader';
import Icon from '@standardsdigital/ui-components/Icon';
import ScreenReaderOnly from '@standardsdigital/ui-components/ScreenReaderOnly';
import type { UserMenuProps } from '@standardsdigital/ui-components/src/components/AppHeader/UserMenu/index';
import LinkButton from '@standardsdigital/ui-components/LinkButton';
import type { LanguageSelectorProps } from '@standardsdigital/ui-components/LanguageSelector';
import getTranslation from '@/util/getTranslation';
import changeLanguageInUrl from '@/util/changeLanguageInUrl';
import styles from './Header.module.css';
import isCartUpdateEvent from '@/components/global/Header/utils/isCartUpdateEvent';
import { searchUrlChangedEvent } from '@/components/pages/SearchPage/utils/urlChangedEvent';
import convertToValidNumber from '@/components/global/Header/utils/convertToValidNumber';
import type { HeaderModel } from '@/redux/services/generatedApi/header';
import type { NullableOptional } from '@/types/util';
import useMenuKeyboardNavigation from '@/hooks/useMenuKeyboardNavigation';
import parseIntegerOrDefault from '@/util/parseIntegerOrDefault';
import defaultSearchInputMaxLength from '@/constants';

export interface HeaderProps {
  translations: string | undefined;
  logo: ReactNode;
  language: string | undefined;
  data: NullableOptional<HeaderModel> | null;
  searchInputMaxLength: string | undefined;
}

function Header({
  translations: jsonTranslations,
  data,
  logo,
  language,
  searchInputMaxLength,
}: Readonly<HeaderProps>) {
  const translations: unknown = JSON.parse(jsonTranslations || '{}');

  const [currentCartCount, setCurrentCartCount] = useState(
    data?.shoppingCart?.itemCount,
  );

  const [, rerender] = useReducer((counter: number) => counter + 1, 1);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const handleCartUpdate = (event: Event) => {
      if (isCartUpdateEvent(event)) {
        setCurrentCartCount(convertToValidNumber(event.detail.count));
      }
    };

    const handleUrlChange = () => {
      rerender();
    };

    document.addEventListener('cartCountUpdated', handleCartUpdate, {
      signal,
    });
    document.addEventListener(searchUrlChangedEvent, handleUrlChange, {
      signal,
    });

    return () => {
      abortController.abort();
    };
  }, []);

  const menuRef = useMenuKeyboardNavigation();

  const {
    navigationLinks = [],
    accountMenu,
    shoppingCart,
    languages = [],
    searchUrl,
  } = data ?? {};

  let links: NavLinkProps['links'] = [];
  if (navigationLinks && navigationLinks.length > 0) {
    links = navigationLinks.map((link) => ({
      link: (
        <a href={link?.url ?? undefined} data-auto="navigation-link">
          {link?.name}
          {link?.invisibleTextTranslation ? (
            <ScreenReaderOnly>{link.invisibleTextTranslation}</ScreenReaderOnly>
          ) : null}
          {link?.isExternal ? <Icon name="ArrowUpRight" /> : null}
        </a>
      ),
      isCurrent: link?.url === globalThis.location.pathname,
    }));
  }

  const navLinkProps: NavLinkProps = {
    links,
    'aria-label': getTranslation(
      'Plugins.Misc.SDTheme.Header.MainNavigation',
      translations,
    ),
    'data-auto': 'navigation-links-header',
  };

  const userMenuProps: UserMenuProps = {
    links: accountMenu?.menuItems?.map((link, index) => ({
      id: index,
      link: (
        <a href={link?.url ?? undefined} data-auto="user-menu-link">
          {link?.name}
          {link?.isExternal ? <Icon name="ArrowUpRight" /> : null}
        </a>
      ),
      isCurrent: link?.isCurrent,
    })),
    firstName: accountMenu?.firstName || undefined,
    lastName: accountMenu?.lastName || undefined,
    logInLink: accountMenu?.loginUrl && (
      <a href={accountMenu?.loginUrl} data-auto="log-in-link-header">
        {getTranslation('Plugins.Misc.SDTheme.Header.LogIn', translations)}
      </a>
    ),
    logOutLink: accountMenu?.logoutUrl ? (
      <a href={accountMenu?.logoutUrl} data-auto="log-out-link-header">
        {getTranslation('Plugins.Misc.SDTheme.Header.LogOut', translations)}
      </a>
    ) : undefined,
    'aria-label': getTranslation(
      'Plugins.Misc.SDTheme.Header.UserMenu',
      translations,
    ),
    createAccountLink: accountMenu?.createAccountUrl ? (
      <a href={accountMenu?.createAccountUrl} data-auto="create-account-link-header">
        {getTranslation('Plugins.Misc.SDTheme.Header.CreateAccount', translations)}
      </a>
    ) : undefined,
    'data-auto': 'user-menu-header',
  };

  const shoppingCartLink = shoppingCart?.url ? (
    <LinkButton
      icon="Cart"
      href={shoppingCart.url}
      variant="text-contrast"
      aria-label={getTranslation(
        currentCartCount === 1
          ? 'Plugins.Misc.SDTheme.Header.YourCartHasItemCount.Singular'
          : 'Plugins.Misc.SDTheme.Header.YourCartHasItemCount.Plural',
        translations,
        currentCartCount ?? '',
      )}
      data-auto="shopping-cart-link-header"
    >
      {currentCartCount}
    </LinkButton>
  ) : undefined;

  const getLanguagesProps = (): LanguageSelectorProps | undefined => {
    if (!language) {
      return undefined;
    }

    const activeLanguageLabel = languages?.find((lang) => lang?.code === language);
    if (!activeLanguageLabel?.name) {
      return undefined;
    }

    return {
      'data-auto': 'language-selector',
      'aria-label': getTranslation(
        'Plugins.Misc.SDTheme.Header.SelectLanguage',
        translations,
      ),
      activeLanguage: {
        code: language,
        label: activeLanguageLabel.name,
      },
      languages:
        languages?.map((lang) => ({
          languageCode: lang?.code ?? '',
          component: (
            <a href={lang?.code ? changeLanguageInUrl(lang.code) : undefined}>
              {lang?.name}
            </a>
          ),
        })) || [],
    };
  };

  const onSearch = (searchTerm: string) => {
    if (searchUrl) {
      globalThis.location.replace(
        searchUrl.replace('%7B%7Bsearchterm%7D%7D', searchTerm),
      );
    }
  };

  const inputMaxLength = parseIntegerOrDefault(
    searchInputMaxLength,
    defaultSearchInputMaxLength,
  );

  const searchPanelProps: AppHeaderProps['searchPanelProps'] = searchUrl
    ? {
        'data-auto': 'search-panel',
        onSubmit: onSearch,
        textInputMaxLength: inputMaxLength,
        textInputLabel: getTranslation(
          'Plugins.Misc.SDTheme.Header.TextInputLabel',
          translations,
        ),
        textInputPlaceholder: getTranslation(
          'Plugins.Misc.SDTheme.Header.TextInputPlaceholder',
          translations,
        ),
        submitButtonText: getTranslation(
          'Plugins.Misc.SDTheme.Header.SubmitButtonText',
          translations,
        ),
        closeButtonText: getTranslation(
          'Plugins.Misc.SDTheme.Header.SearchPanelCloseText',
          translations,
        ),
        searchFormAriaLabel: getTranslation(
          'Plugins.Misc.SDTheme.Header.SearchFormAriaLabel',
          translations,
        ),
        searchButtonText: getTranslation(
          'Plugins.Misc.SDTheme.Header.SearchButtonText',
          translations,
        ),
      }
    : undefined;

  return (
    <AppHeader
      userMenuRef={menuRef}
      className={styles.header}
      logo={
        <a
          href={data?.logo?.url ?? '/'}
          aria-label={getTranslation(
            'Plugins.Misc.SDTheme.Common.HomeLink',
            translations,
          )}
          data-auto="logo-link-header"
        >
          {logo}
        </a>
      }
      hamburgerMenuButtonOpenText={getTranslation(
        'Plugins.Misc.SDTheme.Header.HamburgerMenuButtonOpenText',
        translations,
      )}
      hamburgerMenuButtonCloseText={getTranslation(
        'Plugins.Misc.SDTheme.Header.HamburgerMenuButtonCloseText',
        translations,
      )}
      compactNavigationAriaLabel={getTranslation(
        'Plugins.Misc.SDTheme.Header.CompactNavigationAriaLabel',
        translations,
      )}
      navLinkProps={navLinkProps}
      userMenuProps={userMenuProps}
      shoppingCartLink={shoppingCartLink}
      languageSelectorProps={getLanguagesProps()}
      searchPanelProps={searchPanelProps}
    />
  );
}

export default Header;
